import React, { useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Instructor } from "@models/ISchema";
import { Checkbox, Link, Select, Tooltip } from "@foris/avocado-suite";
import UsableLabel from "@common/components/Autocomplete/usabe-label";
import SimpleResourceList from "@modules/sections/EditSessions/components/simple-resource-list";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { InstructorOption } from "../../hooks/useInstructorSearch";
import styles from "./instructorPicker.module.scss";

interface InstructorPickerProps {
  instructors: Instructor[];
  disabled?: boolean;
  loading?: boolean;
  options: InstructorOption[];
  shouldFilterInstructors: boolean;
  canAssignFromOtherCourses: boolean;
  someDeletedSessionIsSelected?: boolean;
  isReadOnly?: boolean;
  onSearch: (value: string) => void;
  onFilterChange: (checked: boolean) => void;
  onSelect: (instructor: InstructorOption) => void;
  onDelete: (instructor: Instructor) => void;
  onAdvancedSearch?: () => void;
}

const getFormattedLabel = (option: unknown) => {
  const data = option as InstructorOption;
  return <UsableLabel label={`${data.code} - ${data.name}`} isUsable={data?.usable} />;
};

const InstructorPicker = ({
  instructors,
  disabled = false,
  loading = false,
  options,
  shouldFilterInstructors,
  canAssignFromOtherCourses,
  someDeletedSessionIsSelected = false,
  isReadOnly = false,
  onSearch,
  onFilterChange,
  onSelect,
  onDelete,
  onAdvancedSearch,
}: InstructorPickerProps) => {
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();

  const adaptedInstructors = useMemo(() => {
    if (isReadOnly && !instructors?.length) {
      return [
        {
          id: "",
          label: t("assignation-edit.form.notifications.without-assignation"),
          rawData: null,
        },
      ];
    }

    return instructors?.map(instructor => ({
      id: instructor?.code,
      label: instructor?.name,
      rawData: instructor,
      path: isReadOnly && instructor?.id ? editorUrl("instructor", instructor.id) : undefined,
    }));
  }, [instructors, isReadOnly, t]);

  const isFieldDisabled = someDeletedSessionIsSelected || disabled;

  return (
    <div className={styles.instructorPicker}>
      {!isReadOnly && (
        <>
          {!canAssignFromOtherCourses ? (
            <Tooltip
              placement="topLeft"
              label={t(
                "assignation-edit.form.instructor.can-assign-instructors-from-other-courses",
              )}
            >
              <div>
                <Checkbox
                  disabled
                  className={styles.instructorPicker_checkbox}
                  labelRight={t("assignation-edit.form.instructor.only-filtered-instructors")}
                  checked
                />
              </div>
            </Tooltip>
          ) : (
            <Checkbox
              disabled={disabled}
              className={styles.instructorPicker_checkbox}
              labelRight={t("assignation-edit.form.instructor.only-filtered-instructors")}
              checked={shouldFilterInstructors}
              onChange={e => onFilterChange(e.target.checked)}
            />
          )}

          <div
            className={cx(
              styles.instructorPicker_fields,
              !adaptedInstructors?.length && styles.instructorPicker_fields__noSpacing,
              !onAdvancedSearch && styles.instructorPicker_fields__fullWidth,
            )}
          >
            <Select
              isClearable
              disabled={isFieldDisabled}
              isLoading={loading}
              options={options}
              value={null}
              isOptionDisabled={option => !!(option as any)?.isDisabled}
              formatOptionLabel={getFormattedLabel}
              placeholder={t("assignation-edit.form.instructor.selector-placeholder")}
              onInputChange={value => onSearch(value)}
              onChange={(value: InstructorOption) => {
                if (value) onSelect(value);
              }}
            />

            {onAdvancedSearch && (
              <Link
                className={styles.instructorPicker_fields_fieldLink}
                iconRight="external-link"
                size="sm"
                disabled={isFieldDisabled}
                onClick={onAdvancedSearch}
              >
                {t("assignation-edit.form.instructor.advanced-search")}
              </Link>
            )}
          </div>
        </>
      )}

      <SimpleResourceList
        items={adaptedInstructors}
        isDisabled={isFieldDisabled}
        onDeleteResource={
          !isReadOnly ? (instructor: Instructor) => onDelete(instructor) : undefined
        }
      />
    </div>
  );
};

export default InstructorPicker;
